import PropTypes from 'prop-types';

import dayjs from 'dayjs';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import ChatRoom from '../../components/ChatRoom';
import { useEffect, useRef, useState } from 'react';
import MessagingService from '../../utils/messagingService';
import {
  useCreateChatMutation,
  useGetChatQuery,
  useStartChatMutation,
} from '../../services/channels';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { formatError, generateImageAssestUrl } from '@classity-edtech/ui-base';
import { useGetParticipantQuery } from '../../services/participants';
import { MeetingChannelType, MeetingParticipantRole, Roles } from '@classity-edtech/platform-utils';
import { useNavigate, useParams } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import {
  addChat,
  addNewChat,
  addNewMessages,
  ChatMessage,
  clearChat,
  deleteChatMessage,
  editChatMessage,
  updateChat,
} from '../../store/actions/chat';
import { GroupIcon } from '@classity-edtech/ui-base/dist/components/Displays';
import { Chat } from '../../services/models/channels';
import Helmet from '../../components/Helmet';
import { useGetMyProfileQuery } from '../../services/users';

export interface HomeProps extends MuiBoxProps {}

export const ChatRoomPage = ({ className }: HomeProps) => {
  const dispatch = useAppDispatch();
  const { messages } = useAppSelector(state => ({
    messages: state.chatReducer,
  }));
  const messagingRef = useRef<MessagingService | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [sendingMessages, setSendingMessages] = useState(false);
  const navigate = useNavigate();
  const [startChat, { data: startChatData }] = useStartChatMutation();
  const [nextToken, setNextToken] = useState('');
  const [lastMessage, setLastMessage] = useState<{ [key: string]: ChatMessage }>();
  const [channelType, setChannelType] = useState(MeetingChannelType.GROUP);
  const [currentParticipant, setCurrentParticipant] = useState('');
  const [createChat, createChatResult] = useCreateChatMutation();
  const [headerDetails, setHeaderDetails] = useState({
    title: '',
    avatarSrc: '',
    logo: <></>,
    type: MeetingChannelType.GROUP,
  });
  const [editContent, setEditContent] = useState({
    messageId: '',
    content: '',
  });
  const { lessonId } = useParams();

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const isStudent = user?.roleId === Roles.STUDENT;

  const { data: chat, refetch: refetchChat } = useGetChatQuery(
    {
      chatId: lessonId,
    },
    { skip: !user || !lessonId },
  );

  const { data: participant } = useGetParticipantQuery(
    {
      participantId: user?.profile?.ids?.find(id => id?.name === 'uid')?.value,
    },
    { skip: !user },
  );

  const [channelArn, setChannelArn] = useState('');

  const fetchLastMessages = async (Messaging: MessagingService) => {
    try {
      const rooms = isStudent
        ? chat?.rooms?.filter(
            r =>
              !(
                r?.type === MeetingChannelType.P2T &&
                !r?.participants?.some(
                  p => p?.participant?.thirdPartyUid === participant?.thirdPartyUid,
                )
              ),
          )
        : chat?.rooms?.filter(r => r?.type !== MeetingChannelType.P2P);

      rooms?.map(async r => {
        const response = await Messaging.fetchChannelMessages(
          r?.thirdPartyUid,
          null,
          1,
          'DESCENDING',
        );

        setLastMessage(lastMessage => ({
          ...lastMessage,
          [r?.thirdPartyUid]: response?.ChannelMessages?.[0],
        }));
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchChannelMessages = async (
    Messaging: MessagingService,
    type: MeetingChannelType,
    userArn?: string,
  ) => {
    try {
      const channelArn = chat?.rooms?.find(r => {
        if (type === MeetingChannelType.P2P) {
          return r?.type === type && r?.thirdPartyUid !== userArn;
        } else {
          return r?.type === type;
        }
      })?.thirdPartyUid;

      const secondMember = chat?.rooms
        ?.find(r => r?.type === type)
        ?.participants?.find(p => {
          if (type === MeetingChannelType.P2P) {
            return (
              p?.participant?.thirdPartyUid !== participant?.thirdPartyUid &&
              p?.role !== MeetingParticipantRole.MODERATOR
            );
          } else {
            return p?.participant?.thirdPartyUid !== participant?.thirdPartyUid;
          }
        })?.participant?.user?.profile;

      setHeaderDetails({
        title:
          type === MeetingChannelType.GROUP
            ? chat?.title?.text || ''
            : `${secondMember?.firstname || ''} ${secondMember?.lastname || ''}`,
        avatarSrc:
          type === MeetingChannelType.GROUP
            ? ''
            : generateImageAssestUrl(secondMember?.avatar?.contentUrl, 2),
        logo: type === MeetingChannelType.GROUP && <GroupIcon />,
        type,
      });

      const response = await Messaging.fetchChannelMessages(channelArn);

      setNextToken(response?.NextToken);

      dispatch(
        addChat({
          channelArn: channelArn,
          messages: response.ChannelMessages?.map(c => ({
            ...c,
            CreatedTimestamp: c?.CreatedTimestamp?.toString(),
            LastUpdatedTimestamp: c?.LastUpdatedTimestamp?.toString(),
          })),
        }),
      );
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleReceiveMessages = (ChannelMessage: ChatMessage) => {
    if (!messages[ChannelMessage?.ChannelArn]) return;

    if (ChannelMessage && !ChannelMessage.Redacted) {
      dispatch(addNewChat({ channelArn: ChannelMessage?.ChannelArn, messages: [ChannelMessage] }));
      setLastMessage(lastMessage => ({
        ...lastMessage,
        [ChannelMessage?.ChannelArn]: ChannelMessage as ChatMessage,
      }));
    }
  };

  const handleChatCreation = async (participantId): Promise<Chat | null> => {
    const createChatResultResultData = await createChat({
      chatId: lessonId,
      participantId,
    });

    if ('error' in createChatResultResultData) {
      const error = formatError(createChatResultResultData.error.message);

      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );

      return null;
    }

    if ('data' in createChatResultResultData) {
      return createChatResultResultData.data;
    }
  };

  const sendMessage = async (text: string) => {
    if (!messagingRef.current) return;

    try {
      setSendingMessages(true);

      if (editContent?.messageId && editContent?.content) {
        await editMessage(channelArn, editContent?.messageId, text);
      } else {
        const accepter = chat?.rooms
          ?.find(r => r?.type === MeetingChannelType.GROUP)
          ?.participants?.find(
            p => p?.participant?.thirdPartyUid === currentParticipant,
          )?.participantId;

        const fetchedChannelChat =
          !channelArn && (await handleChatCreation(`${participant?.id}-${accepter}`));

        const p2pChannelArn = fetchedChannelChat?.rooms?.find(
          r =>
            r?.type === MeetingChannelType.P2P &&
            r?.participants?.some(p => p?.participant?.thirdPartyUid === currentParticipant),
        )?.thirdPartyUid;

        const response = await messagingRef.current.sendMessage(p2pChannelArn || channelArn, text);

        if (channelArn) {
          const message = {
            Content: text,
            CreatedTimestamp: new Date()?.toString(),
            LastUpdatedTimestamp: new Date()?.toString(),
            MessageId: response?.MessageId,
            Metadata: response?.$metadata,
            Sender: {
              Name: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
              Arn: participant?.thirdPartyUid,
            },
            Status: response?.Status,
          };

          dispatch(
            updateChat({
              channelArn: channelArn,
              messages: [message],
            }),
          );

          setLastMessage(lastMessage => ({
            ...lastMessage,
            [channelArn]: message as ChatMessage,
          }));
        } else {
          refetchChat();
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessages(false);

      setEditContent({
        messageId: '',
        content: '',
      });
    }
  };

  const deleteMessage = async (channelArn: string, messageId: string) => {
    if (!messagingRef.current) return;

    try {
      await messagingRef.current.deleteMessage(channelArn, messageId);

      dispatch(deleteChatMessage({ channelArn, messageId }));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const editMessage = async (channelArn: string, messageId: string, text: string) => {
    if (!messagingRef.current) return;

    try {
      await messagingRef.current.editMessage(channelArn, messageId, text);

      dispatch(editChatMessage({ channelArn, messageId, text }));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const fetchNewMessages = async () => {
    if (!messagingRef.current) return;

    try {
      setLoadingMessages(true);

      const response = await messagingRef.current.fetchChannelMessages(channelArn, nextToken);

      setNextToken(response?.NextToken);

      dispatch(
        addNewMessages({
          channelArn: channelArn,
          messages: response.ChannelMessages?.map(c => ({
            ...c,
            CreatedTimestamp: c?.CreatedTimestamp?.toString(),
            LastUpdatedTimestamp: c?.LastUpdatedTimestamp?.toString(),
          })),
        }),
      );
    } catch (error) {
      console.error('Error fetching new messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleChannelType = async (type: MeetingChannelType, channelArn: string) => {
    if (!messagingRef.current) return;

    if (channelType !== type) {
      setChannelArn(channelArn);
      setChannelType(type);
      setLoadingMessages(true);
      dispatch(clearChat({ channelArn }));
      setNextToken(undefined);
      setCurrentParticipant(undefined);
      await fetchChannelMessages(messagingRef.current, type);
    }
  };

  const handleCurrentStudent = async (userArn: string, channelArn: string) => {
    if (!messagingRef.current) return;

    setChannelArn(channelArn);
    setCurrentParticipant(userArn);
    setChannelType(MeetingChannelType.P2T);
    setLoadingMessages(true);
    dispatch(clearChat({ channelArn }));
    setNextToken(undefined);
    await fetchChannelMessages(messagingRef.current, MeetingChannelType.P2T, userArn);
  };

  const handleCurrentParticipant = async (userArn: string, channelArn: string) => {
    if (!messagingRef.current) return;

    setCurrentParticipant(userArn);
    setChannelType(MeetingChannelType.P2P);
    setChannelArn(channelArn);

    if (
      chat?.rooms?.find(
        r =>
          r?.type === MeetingChannelType.P2P &&
          r?.participants?.some(p => p?.participant?.thirdPartyUid === userArn),
      )
    ) {
      setLoadingMessages(true);
      dispatch(clearChat({ channelArn }));
      setNextToken(undefined);
      await fetchChannelMessages(messagingRef.current, MeetingChannelType.P2P, userArn);
    }
  };

  useEffect(() => {
    try {
      startChat();
    } catch (error) {
      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Unable to establish connection!',
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (startChatData && participant && user && chat) {
      const Messaging = new MessagingService(
        handleReceiveMessages,
        participant?.thirdPartyUid,
        user?.clientRequestToken,
      );

      Messaging.connect(startChatData);

      messagingRef.current = Messaging;

      setHeaderDetails({
        title: chat?.title?.text || '',
        avatarSrc: '',
        logo: <GroupIcon />,
        type: MeetingChannelType.GROUP,
      });

      setChannelArn(chat?.rooms?.find(r => r?.type === MeetingChannelType.GROUP)?.thirdPartyUid);

      fetchLastMessages(Messaging);
      fetchChannelMessages(Messaging, channelType);
    }
  }, [startChatData, participant, user, chat]);

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      localStorage.removeItem('token');
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  const chatMessages =
    (messages[channelArn] ? messages[channelArn] : [])
      ?.filter(m => !!m?.Content)
      ?.map(m => {
        const profile = chat?.rooms
          ?.find(r => r?.type === channelType)
          ?.participants?.find(p => p?.participant?.thirdPartyUid === m?.Sender?.Arn)?.participant
          ?.user?.profile;

        return {
          messageId: m.MessageId,
          avatarSrc: generateImageAssestUrl(profile?.avatar?.contentUrl, 2),
          status: m.Status.Value,
          senderName: `${profile?.firstname || ''} ${profile?.lastname || ''}`,
          sentAt: dayjs(m.CreatedTimestamp).format('HH.mm'),
          text: m.Content,
          chatType: m?.Sender?.Arn === participant?.thirdPartyUid ? 'sender' : 'recipient',
          editAction: () => setEditContent({ messageId: m.MessageId, content: m.Content }),
          deleteAction: () => deleteMessage(channelArn, m.MessageId),
        };
      }) || [];

  const groupChat = {
    avatarSrc: '',
    title: chat?.title?.text || '',
    description:
      lastMessage?.[chat?.rooms?.find(r => r?.type === MeetingChannelType.GROUP)?.thirdPartyUid]
        ?.Content,
    time: dayjs(
      lastMessage?.[chat?.rooms?.find(r => r?.type === MeetingChannelType.GROUP)?.thirdPartyUid]
        ?.CreatedTimestamp,
    ).format('HH.mm'),
    notification: '',
    isActive: channelType === MeetingChannelType.GROUP,
    action: () =>
      handleChannelType(
        MeetingChannelType.GROUP,
        chat?.rooms?.find(c => c?.type === MeetingChannelType.GROUP)?.thirdPartyUid,
      ),
  };

  const tutorProfile = chat?.rooms
    ?.find(r => r?.type === MeetingChannelType.P2T)
    ?.participants?.find(p => p?.participant?.user?.roleId === Roles.TUTOR)?.participant
    ?.user?.profile;

  const participants = chat?.rooms
    ?.find(r => r?.type === MeetingChannelType.GROUP)
    ?.participants?.filter(
      p =>
        p?.role === MeetingParticipantRole.PARTICIPANT &&
        p?.participant?.thirdPartyUid !== participant?.thirdPartyUid,
    );

  const studentsProfiles = chat?.rooms
    ?.filter(r => r?.type === MeetingChannelType.P2T)
    ?.map(r => r?.participants?.filter(p => p?.participant?.user?.roleId === Roles.STUDENT))
    ?.flat();

  const p2tChat = isStudent
    ? {
        avatarSrc: generateImageAssestUrl(tutorProfile?.avatar?.contentUrl, 2),
        title: `${tutorProfile?.firstname || ''} ${tutorProfile?.lastname || ''}`,
        description:
          lastMessage?.[
            chat?.rooms?.find(
              r =>
                r?.type === MeetingChannelType.P2T &&
                r?.participants?.some(p => p?.participantId === participant?.id),
            )?.thirdPartyUid
          ]?.Content,
        time: dayjs(
          lastMessage?.[chat?.rooms?.find(r => r?.type === MeetingChannelType.P2T)?.thirdPartyUid]
            ?.CreatedTimestamp,
        ).format('HH.mm'),
        notification: '',
        isActive: channelType === MeetingChannelType.P2T,
        action: () =>
          handleChannelType(
            MeetingChannelType.P2T,
            chat?.rooms?.find(
              c =>
                c?.type === MeetingChannelType.P2T &&
                c?.participants?.some(p => p?.participantId === participant?.id),
            )?.thirdPartyUid,
          ),
      }
    : undefined;

  const studentsChat = !isStudent
    ? studentsProfiles?.map(p => ({
        avatarSrc: generateImageAssestUrl(p?.participant?.user?.profile?.avatar?.contentUrl, 2),
        title: `${p?.participant?.user?.profile?.firstname || ''} ${p?.participant?.user?.profile?.lastname || ''}`,
        description:
          lastMessage?.[
            chat?.rooms?.find(
              r =>
                r?.participants?.some(
                  c => c?.participant?.thirdPartyUid === p?.participant?.thirdPartyUid,
                ) && r?.type === MeetingChannelType.P2T,
            )?.thirdPartyUid
          ]?.Content,
        time: dayjs(
          lastMessage?.[
            chat?.rooms?.find(
              r =>
                r?.participants?.some(
                  c => c?.participant?.thirdPartyUid === p?.participant?.thirdPartyUid,
                ) && r?.type === MeetingChannelType.P2T,
            )?.thirdPartyUid
          ]?.CreatedTimestamp,
        ).format('HH.mm'),
        notification: '',
        isActive: currentParticipant === p?.participant?.thirdPartyUid,
        action: () =>
          handleCurrentStudent(
            p?.participant?.thirdPartyUid,
            chat?.rooms?.find(
              r =>
                r?.participants?.some(
                  c => c?.participant?.thirdPartyUid === p?.participant?.thirdPartyUid,
                ) && r?.type === MeetingChannelType.P2T,
            )?.thirdPartyUid,
          ),
      }))
    : participants?.map(p => ({
        avatarSrc: generateImageAssestUrl(p?.participant?.user?.profile?.avatar?.contentUrl, 2),
        title: `${p?.participant?.user?.profile?.firstname || ''} ${p?.participant?.user?.profile?.lastname || ''}`,
        description:
          lastMessage?.[
            chat?.rooms?.find(
              r =>
                r?.participants?.some(c => c?.participantId === p?.participantId) &&
                r?.type === MeetingChannelType.P2P,
            )?.thirdPartyUid
          ]?.Content,
        time: dayjs(
          lastMessage?.[
            chat?.rooms?.find(
              r =>
                r?.participants?.some(c => c?.participantId === p?.participantId) &&
                r?.type === MeetingChannelType.P2P,
            )?.thirdPartyUid
          ]?.CreatedTimestamp,
        ).format('HH.mm'),
        notification: '',
        isActive: currentParticipant === p?.participant?.thirdPartyUid,
        action: () =>
          handleCurrentParticipant(
            p?.participant?.thirdPartyUid,
            chat?.rooms?.find(
              r =>
                r?.participants?.some(c => c?.participantId === p?.participantId) &&
                r?.type === MeetingChannelType.P2P,
            )?.thirdPartyUid,
          ),
      }));

  return (
    <>
      <Helmet />
      <ChatRoom
        messages={chatMessages}
        loadingMessages={loadingMessages}
        sendingMessages={sendingMessages}
        sendMessage={sendMessage}
        fetchNewMessages={fetchNewMessages}
        allMessagesFetched={!nextToken}
        groupChatDetails={groupChat}
        p2tChatDetails={p2tChat}
        studentsChatDetails={studentsChat}
        header={headerDetails}
        editContent={editContent}
      />
    </>
  );
};

ChatRoomPage.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ChatRoomPage.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ChatRoomPage)(
  ({ theme }) => `
`,
);
